import type { ComponentProps } from "react";
import { tv, type VariantProps } from "tailwind-variants";

import Button from "@/lib/components/ui/button/button";
import Input from "@/lib/components/ui/input/input";
import Textarea from "@/lib/components/ui/textarea/textarea";
import { cn } from "@/lib/utils";

export default function InputGroup({
  size = "md",
  className,
  ...props
}: ComponentProps<"div"> & { size?: "sm" | "md" | "lg" }) {
  return (
    <div
      role="group"
      data-slot="input-group"
      data-size={size}
      className={cn(
        "group/input-group interactive-group bg-background-neutral border-background-neutral-hovered relative flex w-full min-w-0 items-center rounded-md border shadow-xs",
        "data-[size=lg]:h-11 data-[size=md]:h-10 data-[size=sm]:h-9",
        "has-data-[slot=textarea]:h-auto! has-[textarea]:h-auto!",

        // Variants based on alignment.
        "has-[>[data-align=inline-start]]:[&>input]:pl-2",
        "has-[>[data-align=inline-end]]:[&>input]:pr-2",
        "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
        "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",

        // Error state.
        "has-[[data-slot][aria-invalid=true]]:ring-critical/20 has-[[data-slot][aria-invalid=true]]:border-border-critical",

        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = tv({
  base: "text-foreground-pressed flex shrink-0 h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium select-none [&>svg:not([class*='size-'])]:size-4 [&>kbd]:rounded-[calc(var(--radius)-5px)] group-data-[disabled=true]/input-group:opacity-50",
  variants: {
    align: {
      "inline-start":
        "order-first pl-3 has-[>button]:ml-[-0.35rem] has-[>kbd]:ml-[-0.35rem]",
      "inline-end":
        "order-last pr-3 has-[>button]:mr-[-0.35rem] has-[>kbd]:mr-[-0.35rem]",
      "block-start":
        "order-first w-full justify-start px-3 pt-3 [.border-b]:pb-3 group-has-[>input]/input-group:pt-2.5",
      "block-end":
        "order-last w-full justify-start px-3 pb-3 [.border-t]:pt-3 group-has-[>input]/input-group:pb-2.5",
    },
  },
  defaultVariants: {
    align: "inline-start",
  },
});

export function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      className={cn(inputGroupAddonVariants({ align }), className)}
      data-slot="input-group-addon"
      data-align={align}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonVariants = tv({
  base: "text-sm shadow-none flex gap-2 items-center",
  variants: {
    size: {
      xs: "h-6 md:h-6 gap-1 px-2 rounded-[calc(var(--radius)-5px)] has-icon-size-3.5",
      sm: "h-8 md:h-8 px-2.5 gap-1.5 rounded-md",
    },
    layout: {
      inline: "",
      icon: "px-0 rounded-full",
    },
  },
  compoundVariants: [
    {
      size: "xs",
      layout: "icon",
      className: "size-6 md:size-6",
    },
    {
      size: "sm",
      layout: "icon",
      className: "size-8 md:size-8",
    },
  ],
  defaultVariants: {
    size: "xs",
    layout: "inline",
  },
});

export function InputGroupButton({
  type = "button",
  size = "xs",
  layout = "inline",
  variant = "ghost",
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, "size"> &
  VariantProps<typeof inputGroupButtonVariants>) {
  return (
    <Button
      type={type}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size, layout }), className)}
      data-size={size}
      data-layout={layout}
      {...props}
    />
  );
}

export function InputGroupIconButton(
  props: Omit<ComponentProps<typeof InputGroupButton>, "layout">,
) {
  return <InputGroupButton layout="icon" {...props} />;
}

export function InputGroupText({
  className,
  ...props
}: ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "text-foreground-pressed flex items-center gap-2 text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

export function InputGroupInput({
  className,
  ...props
}: Omit<ComponentProps<"input">, "size"> & { size?: "sm" | "md" | "lg" }) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        "h-auto flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:outline-none",
        className,
      )}
      {...props}
    />
  );
}

export function InputGroupTextarea({
  className,
  ...props
}: ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        "flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:ring-0 focus-visible:outline-none",
        className,
      )}
      {...props}
    />
  );
}
